import type {
  CalculatedAttributeResolver,
  CalculatedAttributeResult,
} from '../../calculatedAttributes/resolve';
import type { LoanTerms } from '../../calculatedAttributes/store';
import { ATTR_NAMES } from '../../types/attrNames';
import { CmsProduct } from '../../types/cmsProduct';
import { PageType, VerticalType } from '../../types/cmsTypes';
import { formatAttributeItemValue } from '../../utils/helpers/attrHelpers';
import {
  getLoanRewardBreakdown,
  productLoanRewardOffers,
} from '../../utils/helpers/personalLoanReward';
import { resolveStackedEffectiveRate } from '../../utils/helpers/stackedEffectiveRate';
import { BrandId } from '../../utils/override';
import {
  DATA_COL_MIN_WIDTHS,
  DATA_COL_WIDTHS,
  IDENTITY_COL_WIDTHS,
  PRICE_COL_WIDTHS,
  TI_IDENTITY_COL_WIDTHS,
} from './constants';
import {
  TableBuilderColumn,
  TableBuilderColumnType,
  TableBuilderMode,
  TableBuilderRow,
  TableBuilderValueCell,
} from './types';

type ColumnShape = Pick<TableBuilderColumn, 'type' | 'attributeKey'>;

/**
 * The engine's answer for the column as a whole — no product, only what the
 * attribute is in this table. `undefined` for a column the engine does not
 * calculate here: its value is the catalogue's.
 */
const columnAnswer = (
  column: ColumnShape,
  resolveCalculated: CalculatedAttributeResolver,
): CalculatedAttributeResult | undefined =>
  column.type === 'CATALOGUE_ATTRIBUTE'
    ? resolveCalculated(column.attributeKey?.name, undefined)
    : undefined;

/**
 * Whether the column is shown at all. Everything renders except a column the
 * engine reports unavailable here: the Travel Insurance price exists in the
 * dictionary so editors can pick it, but no product carries a value for it, so
 * outside Travel Insurance mode it could only ever be a column of "-" — it
 * stays authored in Strapi and simply is not drawn (CMS-1896 AC-8). Loan
 * attributes outside Personal Loan mode keep their authored values.
 */
export const isRenderedColumn = (
  column: ColumnShape,
  mode: TableBuilderMode,
  resolveCalculated: CalculatedAttributeResolver,
): boolean => {
  // Strapi rejects it outside Personal Loan mode on save, but a table switched
  // away from that mode keeps its columns until an editor clears them.
  if (column.type === 'PL_TOTAL_REWARD') return mode === 'PERSONAL_LOAN';
  return columnAnswer(column, resolveCalculated)?.state !== 'unavailable';
};

/**
 * The Travel Insurance price column, which takes a fixed width track at every
 * breakpoint rather than a flexing one (CMS-1896 R7).
 */
export const isTiPriceColumn = (
  column: ColumnShape,
  resolveCalculated: CalculatedAttributeResolver,
): boolean => columnAnswer(column, resolveCalculated)?.provider === 'TI_QUOTE';

function resolveCatalogueCell(
  column: TableBuilderColumn,
  row: TableBuilderRow,
  context: ResolveCellContext,
): TableBuilderValueCell {
  // Calculated attribute (CMS-1896): the value never comes from the catalog.
  // Whether it is calculated in this table is the engine's answer, by the
  // table's mode (CMS-2052).
  const product = row.identityCellProductRef;
  const resolved = context.resolveCalculated(column.attributeKey?.name, product);
  switch (resolved?.provider) {
    case 'TI_QUOTE':
      return {
        id: column.id,
        columnType: column.type,
        calculated: { provider: 'TI_QUOTE', result: resolved },
      };

    case 'PL_CALCULATOR':
      return {
        id: column.id,
        columnType: column.type,
        calculated: {
          provider: 'PL_CALCULATOR',
          result: resolved,
          effectiveRateText: resolveStackedEffectiveRate(
            column.attributeKey?.name,
            product,
            context.brand,
            context.resolveCalculated,
          ),
          highlighted:
            column.attributeKey?.name === ATTR_NAMES.MONTHLY_REPAYMENT,
        },
      };

    default:
      // Not calculated here: the authored value below.
      break;
  }

  const attributes = row.identityCellProductRef?.attributes;
  if (!attributes || !column.attributeKey?.uid) {
    return { id: column.id, text: '-', columnType: column.type };
  }

  const matchingAttr = attributes.find(
    (attr) => attr.name?.uid === column.attributeKey!.uid,
  );

  if (!matchingAttr) {
    return { id: column.id, text: '-', columnType: column.type };
  }

  const formattedValue = formatAttributeItemValue(matchingAttr);
  return {
    id: column.id,
    text: formattedValue || '-',
    freeTextType: 'TEXT_ONLY',
    columnType: column.type,
  };
}

function resolveFreeTextCell(
  column: TableBuilderColumn,
  row: TableBuilderRow,
  colIndex: number,
): TableBuilderValueCell {
  // Fallback when a row has no authored value cell for this column: keep the
  // full `TableBuilderValueCell` shape (incl. the required `columnType`) so the
  // invariant holds and downstream rendering never sees an undefined type.
  return (
    row.valueCell[colIndex] ?? {
      id: column.id,
      text: '-',
      columnType: column.type,
    }
  );
}

/**
 * Build the Hot Reward Pick cell (CMS-1687). Everything is authored on the
 * matching value cell (`row.valueCell[colIndex]`) and mapped 1:1 by fe-services:
 *
 * - `hotRewardGiftTitles` → the in-table cell lines (explicit gift selector).
 * - `rewardOffers` → the curated offer set powering the expanded module.
 * - `link` → the value-cell CTA override URL for the module's Apply Now.
 *
 * The row product + table context supply analytics / CTA fallback only. We
 * assemble the resolved payload here so `ValueCell` / `HotRewardCell` need no
 * extra props threaded through.
 */
function resolveHotRewardCell(
  column: TableBuilderColumn,
  row: TableBuilderRow,
  colIndex: number,
  context: ResolveCellContext,
): TableBuilderValueCell {
  const authoredCell = row.valueCell[colIndex];
  const product = row.identityCellProductRef;
  return {
    id: column.id,
    columnType: column.type,
    hotReward: {
      giftTitles: authoredCell?.hotRewardGiftTitles ?? [],
      rewardOffers: authoredCell?.rewardOffers,
      // CTA override (label + url) for the module's Apply button: the value-cell
      // `link` wins (module-specific), otherwise the row's identity-cell CTA
      // override (the same "Express Checkout"-style label/url shown on the row's
      // apply button). `||` so a cleared "" falls through.
      ctaOverrideUrl: authoredCell?.link?.url || row.identityCellCtaOverrideUrl,
      ctaOverrideLabel:
        authoredCell?.link?.label || row.identityCellCtaOverrideLabel,
      product,
      // Mirror IdentityCell: a manual name override wins over the product name,
      // so the module header matches the name shown in the identity column.
      productName: row.identityCellNameOverride || product?.product_name,
      vertical: context.vertical,
      pageType: context.pageType,
      // Personal Loan mode filters this modal by the module's terms (CMS-1922
      // R4); elsewhere it stays the unfiltered all-offers modal.
      loanTerms:
        context.mode === 'PERSONAL_LOAN' ? context.loanTerms : undefined,
    },
  };
}

/**
 * Build the Total Reward cell (CMS-1922). Nothing is authored on its value cell:
 * the number and the modal come from the row product's offers at the module's
 * loan terms, the same source and formula as the RP.
 */
function resolveTotalRewardCell(
  column: TableBuilderColumn,
  row: TableBuilderRow,
  context: ResolveCellContext,
): TableBuilderValueCell {
  const product = row.identityCellProductRef;
  const { loanTerms } = context;
  if (!loanTerms) {
    return { id: column.id, text: '-', columnType: column.type };
  }

  const offers = productLoanRewardOffers(product);
  return {
    id: column.id,
    columnType: column.type,
    totalReward: {
      breakdown: getLoanRewardBreakdown(offers, loanTerms),
      modal: {
        giftTitles: [],
        rewardOffers: { id: `${column.id}-${row.id}`, ...offers },
        ctaOverrideUrl: row.identityCellCtaOverrideUrl,
        ctaOverrideLabel: row.identityCellCtaOverrideLabel,
        product,
        productName: row.identityCellNameOverride || product?.product_name,
        vertical: context.vertical,
        pageType: context.pageType,
        loanTerms,
      },
    },
  };
}

export type ResolveCellContext = {
  vertical?: VerticalType;
  pageType?: PageType;
  /**
   * The module's own mode — it decides which provider (if any) resolves a
   * calculated column here, so it is not derivable from the column alone.
   */
  mode?: TableBuilderMode;
  /** Market, for the SingSaver-only stacked interest cell (CMS-1921 R4). */
  brand?: BrandId;
  /** The module's own loan terms, Personal Loan mode only (CMS-1922). */
  loanTerms?: LoanTerms;
  /**
   * The engine facade from `useCalculatedAttributeResolver`, passed down so
   * `resolveCell` stays pure and hook-free (CMS-1896 R4). The block never
   * assembles `CalculationSources` itself.
   */
  resolveCalculated: (
    attributeName: string | undefined,
    product: CmsProduct | undefined,
  ) => CalculatedAttributeResult | undefined;
};

export function resolveCell(
  column: TableBuilderColumn,
  row: TableBuilderRow,
  colIndex: number,
  // A caller with no engine resolves nothing — the cell falls through to the
  // authored value, as it does for an ordinary attribute.
  context: ResolveCellContext = { resolveCalculated: () => undefined },
): TableBuilderValueCell {
  switch (column.type) {
    case 'CATALOGUE_ATTRIBUTE':
      return resolveCatalogueCell(column, row, context);
    case 'FREE_TEXT':
      return resolveFreeTextCell(column, row, colIndex);
    case 'HOT_REWARD_PICK':
      return resolveHotRewardCell(column, row, colIndex, context);
    case 'PL_TOTAL_REWARD':
      return resolveTotalRewardCell(column, row, context);
  }

  // Defensive fallback: `column.type` is CMS-authored data, so a value outside
  // the modelled union (e.g. a new backend column type the frontend doesn't yet
  // handle) must not return `undefined` — that would crash `ValueCell` on
  // `cell.id`. Render an empty cell instead so the table keeps working.
  return { id: column.id, text: '-', columnType: column.type };
}

export type ColumnTemplates = Record<'sm' | 'md' | 'lg' | 'xl', string> & {
  /**
   * Whether the templates contain a `minmax(min, 1fr)` track. A table with no
   * data columns has none, so nothing fills the container and the frame has to
   * shrink-wrap the lone identity track instead of framing dead space. A Travel
   * Insurance table whose only column is Price has none either: TI fixes the
   * Price track at every breakpoint (CMS-1896 R7).
   */
  hasFlexibleTrack: boolean;
};

/**
 * Build the `grid-template-columns` value for each breakpoint (CMS-1772 R2,
 * CMS-1896 R7).
 *
 * The identity column is a fixed track; every data column is `minmax(min, 1fr)`,
 * so they share the container's remaining width equally and a column that would
 * fall under its minimum freezes there — pushing the total past the container
 * and handing the overflow to the wrapper's horizontal scroll. 360 and 768 take
 * one minimum for every column type, 1280 and 1440 a per-type one.
 *
 * Travel Insurance mode overrides two tracks and nothing else (CMS-1896 R7): the
 * identity column is wider at every breakpoint (long TI CTA labels), and a Price
 * column is a fixed track at every breakpoint — system-generated content with a
 * known worst case takes no flex share, so the remaining width flows to the
 * attribute columns. Reward and attribute columns behave as in general mode.
 */
export function buildColumnTemplates(
  columns: (Pick<TableBuilderColumn, 'type' | 'attributeKey'> & {
    /** The engine's answer (`isTiPriceColumn`), asked by the caller. */
    isTiPrice?: boolean;
  })[],
  mode: TableBuilderMode = 'GENERAL',
): ColumnTemplates {
  const identityWidths =
    mode === 'TRAVEL_INSURANCE' ? TI_IDENTITY_COL_WIDTHS : IDENTITY_COL_WIDTHS;

  // The caller (`TableBuilder`) filters dormant price columns out before this
  // runs, so `columns` here is the rendered set already: a Price column present
  // means Travel Insurance mode.
  const build = (
    identity: string,
    minWidth: (type: TableBuilderColumnType) => string,
    price: string,
  ) =>
    [
      identity,
      ...columns.map((column) =>
        column.isTiPrice
          ? price
          : `minmax(${minWidth(column.type)}, 1fr)`,
      ),
    ].join(' ');

  // The fallback is unreachable for a typed column — `DATA_COL_MIN_WIDTHS` is
  // total over the union — and fires only for a CMS-authored type the frontend
  // does not model yet. `resolveCell` renders such a column as plain text, so it
  // takes the plain-text column's minimum here to keep content and width
  // resolving the same unknown type the same way.
  const perType = (type: TableBuilderColumnType) =>
    DATA_COL_MIN_WIDTHS[type] ?? DATA_COL_MIN_WIDTHS.FREE_TEXT;

  return {
    sm: build(identityWidths.sm, () => DATA_COL_WIDTHS.sm, PRICE_COL_WIDTHS.sm),
    md: build(identityWidths.md, () => DATA_COL_WIDTHS.md, PRICE_COL_WIDTHS.md),
    lg: build(identityWidths.lg, perType, PRICE_COL_WIDTHS.lg),
    xl: build(identityWidths.xl, perType, PRICE_COL_WIDTHS.xl),
    // A fixed Price track flexes nothing, so a TI table with Price alone has no
    // flexible track and the frame shrink-wraps it, as with no columns at all.
    hasFlexibleTrack: columns.some((column) => !column.isTiPrice),
  };
}
